import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import logoWhite from "@/assets/images/logo/vector/default-monochrome-white.svg";
import { supabase } from "@/integrations/supabase/client";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      setScrollProgress(Math.min(scrollY / 200, 1));
    };
    window.addEventListener("scroll", handleScroll);
    
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      subscription.unsubscribe();
    };
  }, []);

  const menuItems = [
    { name: "ROLES", path: "#roles" },
    { name: "JOURNAL", path: "#journal" },
    { name: "PROFILE", path: "#profile" },
    { name: "CONTACT", path: "#contact" },
  ];

  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname !== '/') {
      navigate('/?section=' + sectionId.replace('#', ''));
      return;
    }

    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    if (window.location.pathname !== '/') {
      navigate('/');
      return;
    }
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMobileMenuOpen(false);
  };

  const navHeight = 72 - (scrollProgress * 8);
  const logoScale = 1 - (scrollProgress * 0.08);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 glass-navbar ${isScrolled ? 'glass-navbar-scrolled' : ''}`}
      style={{
        backgroundColor: `rgba(3, 29, 26, ${0.7 + scrollProgress * 0.2})`,
      }}
    >
      <div className="container mx-auto px-6 md:px-12">
        <nav 
          className="flex items-center justify-between transition-all duration-500 ease-out"
          style={{ height: `${navHeight}px` }}
        >
          <button 
            onClick={scrollToTop} 
            className="transition-transform duration-500 ease-out"
            style={{ 
              height: `${28 * logoScale}px`,
              transform: `scale(${logoScale})`,
              transformOrigin: 'left center'
            }}
          >
            <img 
              src={logoWhite} 
              alt="Kawatteru" 
              className="h-full w-auto opacity-90 hover:opacity-100 transition-opacity duration-500" 
            />
          </button>

          <div className="hidden md:flex items-center space-x-10">
            {menuItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.path)}
                className="text-xs font-display tracking-[0.15em] text-foreground/60 hover:text-foreground/90 transition-all duration-500 ease-out group relative"
              >
                <span className="relative">
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-foreground/30 group-hover:w-full transition-all duration-700 ease-out" />
                </span>
              </button>
            ))}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-foreground/70 hover:text-foreground hover:bg-transparent"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </nav>

        {isMobileMenuOpen && (
          <div className="md:hidden py-8 border-t border-foreground/10">
            <div className="flex flex-col space-y-6">
              {menuItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.path)}
                  className="text-sm font-display tracking-[0.12em] text-foreground/70 hover:text-foreground transition-colors duration-300 px-4 py-2 text-left"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
